import { portalLocale, portalText } from "../../i18n/portal.js";
/**
 * The Do not call screen's words and the small rules behind them (P14-04).
 *
 * Kept apart from the components so each sentence can be asserted on its own, and so the
 * refusal wording for an API code lives in one table rather than in four `catch` blocks that
 * would drift apart.
 */
import { ApiError } from "@wetalk/api-client";
import type { SegmentedOption, TagTone } from "@wetalk/ui";

import type {
  DoNotCallFilterView,
  DoNotCallRowView,
  DoNotCallSourceView,
  DoNotCallUnreadableRowView,
} from "../../api/gateway.js";

/**
 * The design's four filters, in the design's order. `all` is the absence of a filter.
 *
 * AMENDED 2026-09-27 (customer-portal review #256, Konstantina's decision): the fourth is "Added by
 * hand", not the design's "Added by you" — it shows every number anyone in the account (or WeTalk)
 * typed in or imported, and each row's tag now says who.
 */
export const DO_NOT_CALL_FILTER_OPTION: readonly SegmentedOption<DoNotCallFilterView>[] = [
  { value: "all", get label() { return portalText("finance", "m0446"); } },
  { value: "said_on_call", get label() { return portalText("finance", "m1000"); } },
  { value: "opt_out_page", get label() { return portalText("finance", "m1001"); } },
  { value: "added_by_member", get label() { return portalText("finance", "m1002"); } },
];

/**
 * The tag colour for each source, as the prototype draws it: a refusal heard on a call in the
 * second accent, an opt-out form in the first, and anything a person typed in as neutral.
 */
export function sourceTone(source: DoNotCallSourceView): TagTone {
  switch (source) {
    case "said_on_call":
      return "accent-2";
    case "opt_out_page":
      return "accent";
    case "added_by_member":
    case "added_by_operator":
      return "neutral";
  }
}

/**
 * The largest file the import accepts, checked in the browser BEFORE the file is read.
 *
 * 5 MiB is design §15.5.6's ceiling (`WETALK_API_UPLOAD_MAX_BYTE`, `app.sh` states 5242880),
 * measured the way the API measures it: bytes, not characters. This is a courtesy check so a
 * too-large file costs nobody an 11 MiB request; the API stays authoritative, and a
 * `413 payload_too_large` from a lowered server ceiling is still rendered as a refusal.
 */
export const DO_NOT_CALL_FILE_MAX_BYTE = 5 * 1024 * 1024;

/**
 * The most numbers one file may hold — design §15.5.6's row ceiling (`WETALK_API_UPLOAD_MAX_ROW`,
 * `app.sh` states 50000). Stated in the drop zone's hint beside the size (review #253); the API
 * stays authoritative and its refusal names the number it applied.
 */
export const DO_NOT_CALL_FILE_MAX_ROW = 50_000;

export function FILE_TOO_LARGE_TEXT(): string { return portalText("finance", "m1200"); }

export function FILE_UNREADABLE_TEXT(): string { return portalText("finance", "m1201"); }

/** A chosen file with nothing in it (review #252): said, rather than a silently disabled button. */
export function FILE_EMPTY_TEXT(): string { return portalText("finance", "m1202"); }

/** The drop zone's hint, with both ceilings (review #253). */
export function IMPORT_HINT_TEXT(): string { return portalText("finance", "m1203", { value1: countText(DO_NOT_CALL_FILE_MAX_ROW) }); }

const COUNT_FORMAT = () => new Intl.NumberFormat((portalLocale() === "el" ? "el-GR" : "en-IE"));

/** `3,204`. Grouped, because four digits without it misread. */
export function countText(count: number): string {
  return COUNT_FORMAT().format(count);
}


/** What an import did, in the order a person asks: how many are new, how many were not. */
export function importOutcomeText(
  addedCount: number,
  alreadyPresentCount: number,
  unreadableCount: number,
): string {
  const parts = [portalText("finance", "dnc.added", { count: addedCount, value1: countText(addedCount) })];
  if (alreadyPresentCount > 0) parts.push(portalText("finance", "m1004", { value1: countText(alreadyPresentCount) }));
  if (unreadableCount > 0) parts.push(portalText("finance", "dnc.skipped", { count: unreadableCount, value1: countText(unreadableCount) }));
  return `${parts.join(" · ")}.`;
}

const UNREADABLE_REASON: Readonly<Record<DoNotCallUnreadableRowView["problem"], string>> = {
  get missing_number() { return portalText("finance", "m1207"); },
  get invalid_number() { return portalText("finance", "m1208"); },
  get too_many_fields() { return portalText("finance", "m1209"); },
};

/** "Line 4: not a phone number we can read" — which line, and why (review #252). */
export function unreadableRowText(row: DoNotCallUnreadableRowView): string {
  return portalText("finance", "m1006", { value1: String(row.line), value2: UNREADABLE_REASON[row.problem] });
}

/** "and 5 more" under the listed lines, when the API named fewer than it counted. */
export function unreadableMoreText(listed: number, counted: number): string | null {
  return counted > listed ? portalText("finance", "m1007", { value1: countText(counted - listed) }) : null;
}

/**
 * "within a second" — from the AC-30 constant the API sends, never typed into the sentence.
 * The card makes a promise with a number in it, and the number is the contract's.
 */
export function deadlineText(deadlineMs: number): string {
  if (deadlineMs === 1_000) return portalText("finance", "m1008");
  if (deadlineMs < 1_000) return portalText("finance", "m1009", { value1: String(deadlineMs) });
  const second = deadlineMs / 1_000;
  return portalText("finance", "m1010", { value1: Number.isInteger(second) ? String(second) : second.toFixed(1) });
}

/**
 * The sentence under the phrases for the languages that have none.
 *
 * A language without refusal phrases has an incomplete pack, and an incomplete pack makes the
 * language unavailable (§9.4). Nothing listens for an English refusal in its place.
 */
export function missingLanguageText(languageName: readonly string[]): string {
  return portalText("finance", "dnc.unavailable", { count: languageName.length, names: languageName.join(", ") });
}

/** How many languages the card covers, stated beside the heading. */
/**
 * A language's refusal phrases as an owner reads them (customer-portal review #68): the packs list
 * every spelling the matcher needs, including each phrase typed without its apostrophe ("dont call
 * me again" beside "don't call me again"). The matcher needs both; a reader needs one. A phrase
 * whose apostrophe-free form is another phrase's apostrophe-free form is dropped, keeping the
 * first (the pack lists the apostrophe spelling first).
 */
export function readablePhraseOf(phrase: readonly string[]): readonly string[] {
  const seen = new Set<string>();
  const kept: string[] = [];
  for (const one of phrase) {
    const key = one.replaceAll(/['’]/g, "").toLocaleLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    kept.push(one);
  }
  return kept;
}

/** How many phrases a language shows before "Show all". */
export const PHRASE_SHOWN_COUNT = 5;

/**
 * The matcher's rule, in words: a phrase matches anything that STARTS with it, word by word — so
 * a pack may hold the start of a word ("μη με ξαναπαρ…") that catches every ending of it.
 */
export function PHRASE_MATCH_NOTE(): string { return portalText("finance", "m1204"); }

export function coveredText(languagesCoveredCount: number): string {
  return portalText("finance", "dnc.covered", { count: languagesCoveredCount, value1: countText(languagesCoveredCount) });
}

/** The API's longest search (`SEARCH_MAX` in the route); the box stops there (review #251). */
export const SEARCH_MAX = 120;

/** "+302101234567 is on the list · Every agent." — said once the add is saved (review #249). */
export function addedText(row: Pick<DoNotCallRowView, "e164" | "scopeText">): string {
  return portalText("finance", "m1013", { value1: row.e164, value2: row.scopeText });
}

/** What "Remove selected" did (review #254), including any that someone removed first. */
export function removedText(removedCount: number, notFoundCount: number): string {
  const removed = portalText("finance", "dnc.removed", { count: removedCount, value1: countText(removedCount) });
  return notFoundCount === 0 ? removed : `${removed} ${portalText("finance", "dnc.alreadyRemoved", { count: notFoundCount, value1: countText(notFoundCount) })}`;
}

/**
 * The shown-count line to the right of the filters.
 *
 * `kept until removed` is the design's, and it is the fact that matters: an entry never
 * expires on its own.
 */
export function shownText(shownCount: number, matchedCount: number): string {
  return shownCount === matchedCount
    ? portalText("finance", "m1016", { value1: countText(shownCount) })
    : portalText("finance", "m1017", { value1: countText(shownCount), value2: countText(matchedCount) });
}

type Operation = "remove" | "import" | "add" | "export";

/**
 * A refusal, in words, for the API codes each operation documents (design §15.6.7).
 *
 * An error that is not an `ApiError` — the sandbox's, or the network's — is shown as its own
 * message: this screen never replaces a refusal it does not recognise with a reassuring one.
 *
 * AMENDED 2026-09-27 (customer-portal review #244, #253). `validation_failed` reads "not a phone
 * number" only when the API says the phone number was the problem (`field: "e164"`). Every other
 * field's refusal — a name or a note too long, a file not saved as UTF-8, a workbook, a file with
 * no numbers in it — is the API's own sentence, which says what to fix. So are the row ceiling's
 * refusal (it names the number it applied) and "already on the list" (it says for which agents).
 */
export function refusalText(operation: Operation, error: Error): string {
  if (error instanceof ApiError) {
    if (error.code === "validation_failed" && error.field !== "e164") return error.message;
    switch (error.code) {
      case "impersonation_forbidden":
        return portalText("finance", "m1018");
      case "permission_denied":
        return portalText("finance", "m1019");
      case "do_not_call_entry_not_found":
        return portalText("finance", "m1020");
      case "payload_too_large":
        return portalText("finance", "m1021");
      case "contact_source_too_many_rows":
      case "do_not_call_entry_exists":
        return error.message;
      case "validation_failed":
        return portalText("finance", "m1022");
      case "voice_agent_not_found":
        return portalText("finance", "m1023");
      case "export_too_large":
        return portalText("finance", "m1024");
      default:
        break;
    }
  }
  const lead: Readonly<Record<Operation, string>> = {
    remove: portalText("finance", "m1025"),
    import: portalText("finance", "m1026"),
    add: portalText("finance", "m1027"),
    export: portalText("finance", "m1028"),
  };
  return `${lead[operation]} ${error.message}`;
}
